import { CommonNodeViewWrapper } from '@/components/common/node-view-wrapper';
import { LazyMountIframe, useLazyMountIframe } from '@/components/tiptap-editor/tiptap-extension/lazy-mount-tool-iframe';
import { Switch } from '@/components/ui/switch';
import { useIsEditorReadonly } from '@/hooks/use-is-editor-readonly';
import { useNodeViewCollaborationPresence } from '@/hooks/use-node-view-collaboration-presence';
import type { AttributeDef } from '@/lib/parseZyXML';
import { parseZyXML } from '@/lib/parseZyXML';
import { DEFAULT_EDITOR_OPTIONS } from '@/lib/utils';
import { Editor as MonacoEditor } from '@monaco-editor/react';
import { type ReactNodeViewProps } from '@tiptap/react';
import { connectToChild } from 'penpal';
import { memo, useEffect, useRef, useState } from 'react';
import { useDebounceCallback } from 'usehooks-ts';

const OpenStaxPythonCodeRunnerBuilderNodeView: React.FC<ReactNodeViewProps> = ({
    node,
    updateAttributes,
    selected,
    editor,
    decorations,
}) => {
    const isEditorReadonly = useIsEditorReadonly(editor);
    const { id, openStaxPythonCodeRunnerXMLToLoad } = node.attrs;
    const url = `${import.meta.env.VITE_ZYBOOKS_URL}/modern-authoring-tools/open-stax-python-code-runner-builder`;

    const [isDevMode, setIsDevMode] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const exitFullscreenRef = useRef<(() => void) | undefined>(undefined);
    const { shouldMount, isIframeLoaded, handleIframeLoad, resetLoadState } = useLazyMountIframe({
        containerRef,
        editor,
        forceMount: selected,
        id,
    });
    const { presenceUsers, presenceTracking } = useNodeViewCollaborationPresence(editor, id, 'openStaxPythonCodeRunnerBuilder', selected);

    const parseZyXMLConfig = {
        rootTag: 'zyTool',
        xmlPropName: 'openStaxPythonCodeRunnerXMLToLoad',
        attributes: [
            { name: 'name', type: 'string' },
            { name: 'caption', type: 'string' },
            { name: 'challenge', type: 'boolean' },
        ] as AttributeDef[],
        hasOptions: true,
        hasInstructions: true,
    };

    const handleUpdateOpenStaxPythonCodeRunnerXML = useDebounceCallback((openStaxPythonCodeRunnerXMLToLoad: string) => {
        const updates = parseZyXML(openStaxPythonCodeRunnerXMLToLoad, parseZyXMLConfig);
        if (updates) updateAttributes(updates);
    }, 300);

    useEffect(() => {
        if (!shouldMount || isDevMode || !iframeRef.current) {
            return;
        }

        const connection = connectToChild({
            iframe: iframeRef.current,
            methods: {
                updateParentXML: (XML: string) => {
                    handleUpdateOpenStaxPythonCodeRunnerXML(XML);
                },
                getParentXML: () => {
                    return openStaxPythonCodeRunnerXMLToLoad;
                },
                getBaseImageUrl: () => {
                    return editor.state.doc.attrs.baseImageUrl;
                },
                notifyEscapeKeyPressed: () => {
                    exitFullscreenRef.current?.();
                },
            },
        });

        return () => connection.destroy();
    }, [shouldMount, isDevMode]); // eslint-disable-line react-hooks/exhaustive-deps -- We don't want to re-run this effect when openStaxPythonCodeRunnerXMLToLoad changes

    const handleDevModeChange = (checked: boolean) => {
        resetLoadState();
        setIsDevMode(checked);
    };

    const mainContent = isDevMode ? (
        <MonacoEditor
            defaultLanguage="xml"
            value={openStaxPythonCodeRunnerXMLToLoad}
            onChange={value => handleUpdateOpenStaxPythonCodeRunnerXML(value || '')}
            options={{ ...DEFAULT_EDITOR_OPTIONS, readOnly: isEditorReadonly }}
            theme="vs"
        />
    ) : (
        <LazyMountIframe
            containerRef={containerRef}
            shouldMount={shouldMount}
            id={id}
            url={url}
            title="OpenStax Python"
            iframeRef={iframeRef}
            loadingMessage="Loading OpenStax Python…"
            isIframeLoaded={isIframeLoaded}
            onLoad={handleIframeLoad}
        />
    );

    const settingsPanel = (
        <div className="bg-gray-100 px-4 py-2 pe-10 dark:bg-gray-800 flex items-center justify-end border-b gap-x-2">
            <label className="flex items-center gap-2 text-xs font-medium text-gray-600 dark:text-gray-400">
                Dev mode:
                <Switch
                    checked={isDevMode}
                    disabled={isEditorReadonly}
                    onCheckedChange={handleDevModeChange}
                />
            </label>
        </div>
    );

    return (
        <CommonNodeViewWrapper
            decorations={decorations}
            label="OpenStax Python"
            selected={selected}
            settingsPanel={settingsPanel}
            contentClassName="mx-0"
            presenceTracking={presenceTracking}
            presenceUsers={presenceUsers}
            fullscreenContent={mainContent}
            exitFullscreenRef={exitFullscreenRef}
            fullscreenMode="inline"
        >
            {mainContent}
        </CommonNodeViewWrapper>
    );
};

export default memo(OpenStaxPythonCodeRunnerBuilderNodeView);
